// The merge behind References → Senders: one row per address-or-domain,
// carrying BOTH facts a document's inbound path can depend on — whether the
// sender may email documents into intake (`intake_senders`) and, for a whole
// domain, which company an upload or email from it files to
// (`company_email_domains`, "the uploader-domain rung"). Pure and testable on
// purpose: screens do not talk to the transport, and the mock harness can only
// reach `src/lib`.
//
// The join is EXACT-STRING, never widened. `jane@x.test` (an address, checked
// by the intake gate) and `@x.test` (a domain, checked by the company-routing
// rung) are two different comparisons in the backend and stay two different
// entries here — collapsing them would imply a relationship neither rung
// actually has.
//
// BOTH TABLES ALLOW MANY ROWS PER KEY. The unique indexes on both are partial
// (`WHERE is_active`), so retiring a row frees its string for a brand-new row
// with a different id (`app/mock/tests/intake-senders.test.ts` asserts this
// for intake senders; the same partial-index shape holds for domains). A
// `SenderEntry` that collapsed each side to one nullable row would silently
// drop whichever row the join didn't keep, and could hand a caller a retired
// row's id/`row_version` to PATCH — which would either land on dead data or,
// worse, come back a raw uniqueness violation. So each side is AT MOST ONE
// active row (`intake` / `routing`) plus every other row for that key
// (`intakeHistory` / `routingHistory`), and only the active row is ever
// written to.
import type { CompanyEmailDomain, IntakeSender } from './types'

export interface SenderEntry {
  key: string
  isDomain: boolean
  intake: IntakeSender | null
  intakeHistory: IntakeSender[]
  routing: CompanyEmailDomain | null
  routingHistory: CompanyEmailDomain[]
}

/** Merge the two masters on their exact stored string. Deterministic order —
    `key.localeCompare` — so the tab's row order doesn't shuffle on reload.
    No filtering: whether a retired entry is worth showing is the caller's
    business (the Senders tab's "Show retired" switch). */
export function mergeSenderEntries(
  senders: readonly IntakeSender[],
  domains: readonly CompanyEmailDomain[],
): SenderEntry[] {
  const byKey = new Map<string, SenderEntry>()
  const entryFor = (key: string): SenderEntry => {
    let e = byKey.get(key)
    if (!e) {
      e = { key, isDomain: key.startsWith('@'), intake: null, intakeHistory: [], routing: null, routingHistory: [] }
      byKey.set(key, e)
    }
    return e
  }
  for (const s of senders) {
    const e = entryFor(s.address)
    if (s.is_active) e.intake = s
    else e.intakeHistory.push(s)
  }
  for (const d of domains) {
    const e = entryFor(d.domain)
    if (d.is_active) e.routing = d
    else e.routingHistory.push(d)
  }
  return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key))
}

/** An entry is "active" if either side of it is — a sender allowed to email
    in but not routed anywhere is still active, and so is a domain routed
    somewhere but not allowed to email in. */
export function entryIsActive(e: SenderEntry): boolean {
  return e.intake !== null || e.routing !== null
}

/**
 * The company an entry's routing resolves to, by NAME — or `null` when there
 * is no active routing row at all.
 *
 * The MAP MUST WIN over the row's own `company_name`. `PATCH
 * /company-email-domains/{id}` (unlike the scanner patch beside it) never
 * refreshes the denormalized `company_name` column, so after a re-point the
 * stored name is the OLD company's — in production and in the mock alike.
 * The matching ladder is unaffected (it resolves the name from the companies
 * master, never from this column), so this is a display-only staleness, and
 * resolving client-side is the fix rather than a route change.
 */
export function routedCompanyName(e: SenderEntry, companyNames: ReadonlyMap<string, string>): string | null {
  if (e.routing === null) return null
  return companyNames.get(e.routing.company_id) ?? e.routing.company_name ?? e.routing.company_id
}
